import {
  Button,
  Field,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
} from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useStores } from '../../../common/hooks';
import { parseEmailAddresses } from '../../../shared/functions';
import { ValidationMessageList } from '../../shared-components';
import './IndividualRecipientInput.scss';

const MAX_EMAIL_LENGTH = 250;

const IndividualRecipientInputImpl: React.FC = () => {
  const {
    createOrEditEmailTemplatePageUiStore,
  } = useStores();

  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const validationMessages = createOrEditEmailTemplatePageUiStore.getIndividualRecipientValidationMessages(isBeingValidated);

  const handleSubmitEmailAddresses = (emailAddresses: string[]): void => {
    createOrEditEmailTemplatePageUiStore.addIndividualRecipients(emailAddresses);
    setIsBeingValidated(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    createOrEditEmailTemplatePageUiStore.setIndividualRecipientsInput(event.target.value || undefined);
    if (!event.target.value) setIsBeingValidated(false);
  };

  const handleAddIndividualRecipients = (): void => {
    const emailAddresses = parseEmailAddresses(createOrEditEmailTemplatePageUiStore.individualRecipientsInput || '');
    if (!emailAddresses.length) return;

    handleSubmitEmailAddresses(emailAddresses);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    const pastedText = event.clipboardData.getData('text/plain');
    if (!pastedText) return;

    const pastedEmailAddresses = parseEmailAddresses(pastedText);
    if (!pastedEmailAddresses.length) return;

    event.preventDefault();
    handleSubmitEmailAddresses(pastedEmailAddresses);
  };

  return (
    <div
      className='individual-recipient-input'
    >
      <Field
        label='Individual recipients'
        validationMessage={validationMessages.length
          ? <ValidationMessageList messages={validationMessages} />
          : undefined}
        validationMessageIcon={null}
        hint={(
          <>
            <b>💡 Pro tip</b>
            <div
              className='tips-info'
            >
              <p>Paste multiple email addresses at once, Separate them with:</p>
              <ul>
                <li>Commas: email1@test.com, email2@test.com</li>
                <li>Semicolons: email1@test.com; email2@test.com</li>
                <li>Line breaks (copy from spreadsheet)</li>
              </ul>
            </div>
          </>
        )}
      >
        <TagPicker
          noPopover
          size='large'
          selectedOptions={createOrEditEmailTemplatePageUiStore.individualRecipients}
          onOptionSelect={(_, data) => createOrEditEmailTemplatePageUiStore
            .setEmailTemplateRequestBody<string[]>({
              key: 'individualRecipients',
              value: data.selectedOptions,
            })}
        >
          <TagPickerControl
            secondaryAction={(
              <span className='individual-recipient-input__actions'>
                <Button
                  appearance='primary'
                  size='small'
                  disabled={!createOrEditEmailTemplatePageUiStore.individualRecipientsInput}
                  onClick={handleAddIndividualRecipients}
                >
                  Add
                </Button>
                {
                  createOrEditEmailTemplatePageUiStore.hasIndividualRecipients && (
                    <Button
                      appearance='transparent'
                      size='small'
                      onClick={() => createOrEditEmailTemplatePageUiStore
                        .setEmailTemplateRequestBody<string[]>({
                          key: 'individualRecipients',
                          value: [],
                        })}
                    >
                      Clear all
                    </Button>
                  )
                }
              </span>
            )}
          >
            <TagPickerGroup aria-label='Selected individual recipients'>
              {
                createOrEditEmailTemplatePageUiStore.individualRecipients.map((emailAddress) => (
                  <Tag
                    key={emailAddress}
                    value={emailAddress}
                  >
                    {emailAddress}
                  </Tag>
                ))
              }
            </TagPickerGroup>
            <TagPickerInput
              value={createOrEditEmailTemplatePageUiStore.individualRecipientsInput || ''}
              maxLength={MAX_EMAIL_LENGTH}
              onChange={handleInputChange}
              onPaste={handlePaste}
            />
          </TagPickerControl>
        </TagPicker>
      </Field>
    </div>
  )
}

export const IndividualRecipientInput = observer(IndividualRecipientInputImpl);
